(() => {
  const FAMILY_ORDER = ['tablet', 'watch', 'phone', 'charging', 'accessories'];
  const COMMERCIAL_TIER_ORDER = { flagship: 0, core: 1, maintain: 2, review: 3 };
  const MANIFEST_CACHE = new Map();
  const MANIFEST_TTL = 5 * 60 * 1000;
  const MAX_MANIFEST_CACHE_ENTRIES = 8;
  const GLOBAL_UNKNOWN_MODEL_FALLBACK = new Set([
    'apple-watch', 'apple-watch-ultra', 'galaxy-watch', 'galaxy-watch-ultra', 'pixel-watch',
    'galaxy-tab', 'surface-pro', 'surface-go', 'iphone', 'galaxy-phone', 'pixel-phone'
  ]);

  const MODEL_PATTERNS = [
    ['ipad-pro', /\bipad\s+pro\b/i],
    ['ipad-air', /\bipad\s+air\b/i],
    ['ipad-mini', /\bipad\s+mini\b/i],
    ['ipad', /\bipad\b(?!\s+(?:pro|air|mini))/i],
    ['galaxy-tab', /\b(?:samsung\s+)?galaxy\s+tab\b/i],
    ['surface-pro', /\b(?:microsoft\s+)?surface\s+pro\b/i],
    ['surface-go', /\b(?:microsoft\s+)?surface\s+go\b/i],
    ['surface', /\b(?:microsoft\s+)?surface\b(?!\s+(?:pro|go))/i],
    ['apple-watch-ultra', /\bapple\s+watch\s+ultra\b/i],
    ['apple-watch', /\bapple\s+watch\b(?!\s+ultra)/i],
    ['galaxy-watch-ultra', /\b(?:samsung\s+)?galaxy\s+watch(?:\s+\d{1,2})?\s+ultra\b/i],
    ['galaxy-watch', /\b(?:samsung\s+)?galaxy\s+watch\b(?!\s+(?:\d{1,2}\s+)?ultra)/i],
    ['pixel-watch', /\b(?:google\s+)?pixel\s+watch\b/i],
    ['garmin', /\bgarmin\b/i],
    ['fitbit', /\bfitbit\b/i],
    ['iphone', /\b(?:apple\s+)?iphone\b/i],
    ['galaxy-phone', /\b(?:samsung\s+)?galaxy\s+(?:(?:s|a|note)\s*\d|z\s*(?:fold|flip))/i],
    ['pixel-phone', /\b(?:google\s+)?pixel\b(?!\s+watch)/i],
    ['motorola-razr', /\b(?:motorola\s+)?razr\b/i],
    ['chargestation', /\bcharge\s*station\b/i],
    ['wireless-charging', /\b(?:wireless\s+charg|qi\s*2?\s+charg|magsafe\s+charg)/i]
  ];

  const GENERATION_PATTERNS = {
    'apple-watch': [
      /\b(?:apple\s*watch\s*)?(?:series\s*[- ]?\s*(?:se(?:\s*\d{1,2})?|\d{1,3})|s\s*[- ]?\s*(?:se(?:\s*\d{1,2})?|\d{1,3}))(?!\w)/gi
    ],
    'apple-watch-ultra': [
      /\b(?:apple\s*watch\s*)?ultra(?:\s*\d{1,2})?(?!\w)/gi
    ],
    'galaxy-watch': [
      /\b(?:samsung\s*)?(?:galaxy\s*)?watch\s*\d{1,2}(?:\s*(?:classic|pro))?(?!\w)/gi,
      /\b(?:samsung\s*)?(?:galaxy\s*)?watch\s*fe(?!\w)/gi
    ],
    'galaxy-watch-ultra': [
      /\b(?:samsung\s*)?(?:galaxy\s*)?watch(?:\s*\d{1,2})?\s*ultra(?:\s*\d{1,2})?(?!\w)/gi
    ],
    'pixel-watch': [
      /\b(?:google\s*)?pixel\s*watch\s*\d{1,2}(?!\w)/gi
    ],
    iphone: [
      /\b(?:apple\s*)?iphone\s*se\s*(?:20\d{2}|(?:1st|2nd|3rd)\s*(?:gen|generation))\b/gi,
      /\b(?:apple\s*)?iphone\s*(?:air|se)(?!\w|\s+(?:\d|(?:1st|2nd|3rd)\s*(?:gen|generation)))/gi,
      /\b(?:apple\s*)?iphone\s*(?:\d{1,2}|x(?:s|r)?)(?:\s*(?:pro\s*max|pro|max|plus|mini|e))?(?!\w)/gi
    ],
    'galaxy-phone': [
      /\b(?:samsung\s*)?(?:galaxy\s*)?(?:s|a)\s*\d{1,3}(?:\s*(?:ultra|plus|\+|fe))?(?![\w+])/gi,
      /\b(?:samsung\s*)?(?:galaxy\s*)?note\s*\d{1,3}(?:\s*ultra)?(?!\w)/gi,
      /\b(?:samsung\s*)?(?:galaxy\s*)?z\s*(?:fold|flip)\s*\d{1,2}(?!\w)/gi
    ],
    'pixel-phone': [
      /\b(?:google\s*)?pixel\s*fold(?!\w|\s+\d)/gi,
      /\b(?:google\s*)?pixel\s*\d{1,2}[a-z]?(?:\s*(?:pro\s*xl|pro|xl|fold))?(?!\w)/gi
    ],
    ipad: [
      /\b\d{1,2}(?:st|nd|rd|th)\s*(?:gen|generation)\b/gi,
      /\b20(?:1[6-9]|2\d)\b/g
    ],
    'ipad-pro': [
      /\b(?:m[1-5]|20(?:1[6-9]|2\d))\b/gi
    ],
    'ipad-air': [
      /\b(?:m[1-5]|20(?:1[6-9]|2\d))\b/gi
    ],
    'ipad-mini': [
      /\b(?:a1[4-9]|20(?:1[6-9]|2\d))\b/gi
    ],
    'galaxy-tab': [
      /\b(?:samsung\s*)?(?:galaxy\s*)tab\s*(?:s|a)\s*\d{1,2}(?:\s*(?:ultra|plus|\+|fe|lite)){0,2}(?![\w+])/gi
    ],
    'surface-pro': [
      /\b(?:microsoft\s*)?surface\s*pro\s*\d{1,2}(?!\w)/gi
    ],
    'surface-go': [
      /\b(?:microsoft\s*)?surface\s*go\s*\d{1,2}(?!\w)/gi
    ]
  };

  const BARE_OPTION_PATTERNS = {
    iphone: [/\b\d{1,2}(?:\s*(?:pro\s*max|pro|max|plus|mini|e))?(?!\w)/gi],
    'pixel-phone': [/\b\d{1,2}[a-z]?(?:\s*(?:pro\s*xl|pro|xl|fold))?(?!\w)/gi],
    'galaxy-tab': [/\b(?:s|a)\s*\d{1,2}(?:\s*(?:ultra|plus|\+|fe|lite)){0,2}(?![\w+])/gi],
    'surface-pro': [/\bpro\s*\d{1,2}(?!\w)/gi],
    'surface-go': [/\bgo\s*\d{1,2}(?!\w)/gi]
  };

  const PHONE_MODEL_KEYS = new Set(['iphone', 'galaxy-phone', 'pixel-phone']);
  const TRAILING_FIT_EDITION_PATTERN = /^(?:\s+|\s*[-/]\s*)(?:pro\s+max|pro\s+xl|fan\s+edition|air|edge|fold|flip|lite|slim|neo|core|ultra|plus|fe|pro|max|mini|xl|e|4g|5g)\b/i;

  const comparisonKey = (value) => String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/pro\s*max/gi, 'pro max')
    .replace(/pro\s*xl/gi, 'pro xl')
    .replace(/z\s*fold/gi, 'z fold')
    .replace(/z\s*flip/gi, 'z flip')
    .replace(/\+/g, ' plus ')
    .toLowerCase()
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-z])/g, '$1 $2')
    .replace(/\bgen\b/g, 'generation')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const normalizeText = (value) => String(value ?? '').replace(/[\s\u00a0]+/g, ' ').trim();
  const scopeKey = (family, model) => `${family}::${model}`;
  const exactUnknownKey = (raw) => `unmapped:${encodeURIComponent(String(raw ?? ''))}`;
  const unknownFamilyKey = (raw) => `unmapped-family:${encodeURIComponent(String(raw ?? ''))}`;
  const unknownModelKey = (raw) => `unmapped-model:${encodeURIComponent(String(raw ?? ''))}`;
  const DEVICE_OPTION_NAME_PATTERN = /\b(?:device|model|generation|compatibility|compatible|iphone|phone|pixel|galaxy|watch|ipad|tablet|surface)\b/i;
  const DEVICE_SIZE_OPTION_PATTERN = /\bsize\b/i;
  const BARE_STRUCTURED_MODEL_KEYS = new Set(['galaxy-tab', 'surface-pro', 'surface-go']);
  const sourceSupportsBareEdition = (source, modelKey) => source.kind === 'metafield-generation'
    || (source.kind === 'option' && DEVICE_OPTION_NAME_PATTERN.test(source.name))
    || (BARE_STRUCTURED_MODEL_KEYS.has(modelKey) && (
      source.kind === 'tag'
      || (source.kind === 'option' && DEVICE_SIZE_OPTION_PATTERN.test(source.name))
    ));
  const activeState = (state) => Boolean(state.family || state.model || state.generation);

  const toRawValues = (value) => {
    if (Array.isArray(value)) {
      return value.flatMap(toRawValues);
    }
    if (value && typeof value === 'object') {
      if ('value' in value) {
        return toRawValues(value.value);
      }
      if ('name' in value) {
        return toRawValues(value.name);
      }
      return [];
    }
    const normalized = normalizeText(value);
    return normalized ? [normalized] : [];
  };

  const ensureUnmappedLog = () => {
    if (!Array.isArray(window.AvonticDeviceUnmapped)) {
      window.AvonticDeviceUnmapped = [];
    }
    return window.AvonticDeviceUnmapped;
  };

  const unmappedKeys = new Set();
  const logUnmapped = ({ kind, raw, family = '', model = '', record = {}, source = {} }) => {
    const value = normalizeText(raw);
    if (!value) {
      return;
    }

    const key = [kind, family, model, value, record.id || record.handle || '', source.id || source.name || ''].join('|');
    if (unmappedKeys.has(key)) {
      return;
    }
    unmappedKeys.add(key);

    const entry = {
      kind,
      raw: value,
      family,
      model,
      productId: record.id || '',
      productHandle: record.handle || '',
      source: source.name || source.kind || ''
    };
    ensureUnmappedLog().push(entry);
  };

  const parseRegistry = (root) => {
    const node = root.querySelector('[data-av-device-aliases]');
    if (!node) {
      throw new Error('Avontic device alias registry is missing.');
    }

    const raw = JSON.parse(node.textContent);
    const registry = {
      version: raw.version || 'unversioned',
      families: raw.families || [],
      models: raw.models || [],
      generations: raw.generations || [],
      familyByKey: new Map(),
      familyByAlias: new Map(),
      modelByKey: new Map(),
      modelByAliasByFamily: new Map(),
      generationsByScope: new Map(),
      generationByAliasByScope: new Map()
    };

    registry.families.forEach((family) => {
      registry.familyByKey.set(family.key, family);
      [family.key, family.label, ...(family.aliases || [])].forEach((alias) => {
        registry.familyByAlias.set(comparisonKey(alias), family);
      });
    });

    registry.models.forEach((model) => {
      registry.modelByKey.set(model.key, model);
      if (!registry.modelByAliasByFamily.has(model.family)) {
        registry.modelByAliasByFamily.set(model.family, new Map());
      }
      [model.key, model.label, ...(model.aliases || [])].forEach((alias) => {
        registry.modelByAliasByFamily.get(model.family).set(comparisonKey(alias), model);
      });
    });

    registry.generations.forEach((generation) => {
      const key = scopeKey(generation.family, generation.model);
      if (!registry.generationsByScope.has(key)) {
        registry.generationsByScope.set(key, []);
        registry.generationByAliasByScope.set(key, new Map());
      }
      registry.generationsByScope.get(key).push(generation);
      [generation.key, generation.label, ...(generation.aliases || [])].forEach((alias) => {
        const aliasKey = comparisonKey(alias);
        const existing = registry.generationByAliasByScope.get(key).get(aliasKey);
        if (!existing) {
          registry.generationByAliasByScope.get(key).set(aliasKey, generation);
        } else if (existing.key !== generation.key) {
          console.warn('[Avontic device registry] Duplicate alias inside one exact-fit scope.', {
            scope: key,
            alias,
            first: existing.key,
            ignored: generation.key
          });
        }
      });
    });

    registry.generationsByScope.forEach((entries) => {
      entries.sort((left, right) => (Number(right.rank) || 0) - (Number(left.rank) || 0) || left.label.localeCompare(right.label, undefined, { numeric: true }));
    });

    return registry;
  };

  const canonicalFamily = (raw, registry) => registry.familyByAlias.get(comparisonKey(raw)) || null;

  const canonicalModel = (raw, family, registry) => {
    if (!raw) {
      return null;
    }
    if (family) {
      return registry.modelByAliasByFamily.get(family)?.get(comparisonKey(raw)) || null;
    }
    const matches = registry.models.filter((model) => {
      const map = registry.modelByAliasByFamily.get(model.family);
      return map?.get(comparisonKey(raw))?.key === model.key;
    });
    return matches.length === 1 ? matches[0] : null;
  };

  const canonicalGeneration = (raw, family, model, registry) => registry.generationByAliasByScope
    .get(scopeKey(family, model))
    ?.get(comparisonKey(raw)) || null;

  const mappedGenerationChoice = (entry) => ({
    key: entry.key,
    label: entry.label,
    rank: Number(entry.rank) || 0,
    mapped: true,
    urlValue: entry.key
  });

  const unknownGenerationRank = (label, model) => {
    const key = comparisonKey(label);
    const editionPriority = /\b(?:pro max|pro xl|ultra)\b/.test(key)
      ? 4
      : /\b(?:pro|plus|fold|flip|air|edge)\b/.test(key)
        ? 3
        : /\b(?:fe|mini|lite|slim|xl|e)\b/.test(key)
          ? 2
          : 1;
    let match;

    if (model === 'apple-watch' && (match = key.match(/\b(?:series|s) (\d{1,3})\b/))) {
      return 1100 + Number(match[1]);
    }
    if (model === 'iphone' && (match = key.match(/\biphone (\d{1,2})\b/))) {
      return Number(match[1]) * 100 + editionPriority;
    }
    if (model === 'galaxy-phone') {
      if ((match = key.match(/\b(?:galaxy )?s (\d{1,3})\b/))) {
        return Number(match[1]) * 100 + editionPriority;
      }
      if ((match = key.match(/\b(?:galaxy )?a (\d{1,3})\b/))) {
        return 500 + Number(match[1]);
      }
      if ((match = key.match(/\b(?:galaxy )?z fold (\d{1,2})\b/))) {
        return 700 + Number(match[1]);
      }
      if ((match = key.match(/\b(?:galaxy )?z flip (\d{1,2})\b/))) {
        return 650 + Number(match[1]);
      }
      if ((match = key.match(/\b(?:galaxy )?note (\d{1,3})\b/))) {
        return 400 + Number(match[1]);
      }
    }
    if (model === 'pixel-phone' && (match = key.match(/\bpixel (\d{1,2})/))) {
      return Number(match[1]) * 100 + editionPriority;
    }
    return Number(key.match(/\d{1,4}/)?.[0]) || -1;
  };

  const unknownGenerationChoice = (raw, context) => {
    const label = normalizeText(raw);
    logUnmapped({ ...context, kind: 'generation', raw: label });
    return {
      key: exactUnknownKey(label),
      label,
      rank: unknownGenerationRank(label, context.model),
      mapped: false,
      urlValue: label
    };
  };

  const detectModels = (value, registry) => {
    const text = comparisonKey(value);
    const choices = new Map();
    MODEL_PATTERNS.forEach(([key, pattern]) => {
      const model = registry.modelByKey.get(key);
      if (model && pattern.test(text)) {
        choices.set(key, model);
      }
    });
    if (choices.has('apple-watch-ultra') && /\b(?:series|s)\s*(?:se|\d{1,3})\b/.test(text)) {
      const appleWatch = registry.modelByKey.get('apple-watch');
      if (appleWatch) {
        choices.set(appleWatch.key, appleWatch);
      }
    }
    return choices;
  };

  const inferFamilies = (value) => {
    const text = comparisonKey(value);
    const families = new Set();
    if (/\b(?:apple\s+watch|galaxy\s+watch|pixel\s+watch|garmin|fitbit|smartwatch|watch)\b/.test(text)) {
      families.add('watch');
    }
    if (/\b(?:ipad|galaxy\s+tab|surface|tablet)\b/.test(text)) {
      families.add('tablet');
    }
    if (/\b(?:iphone|galaxy\s+(?:s|a|note|z\s+(?:fold|flip))|google\s+pixel|motorola\s+razr|smartphone|phone)\b/.test(text)) {
      families.add('phone');
    }
    if (/\b(?:charge\s*station|wireless\s+charg|charger|charging|power\s+bank|magsafe|qi\s*2?)\b/.test(text)) {
      families.add('charging');
    }
    return families;
  };

  const sourceListForPayload = (payload = {}) => {
    const sources = [];
    const add = (kind, name, value, context = value) => {
      const normalized = normalizeText(value);
      if (!normalized) {
        return;
      }
      sources.push({
        id: `${kind}:${sources.length}`,
        kind,
        name,
        value: normalized,
        context: normalizeText(context)
      });
    };

    add('title', 'product.title', payload.title);
    add('product-type', 'product.productType', payload.productType);
    add('template-suffix', 'product.templateSuffix', payload.templateSuffix);
    toRawValues(payload.tags).forEach((tag) => add('tag', 'product.tags', tag));
    (Array.isArray(payload.options) ? payload.options : []).forEach((option) => {
      const optionName = normalizeText(option?.name);
      toRawValues(option?.values).forEach((value) => {
        add('option', `option ${optionName || 'value'}`, value, `${optionName} ${value}`);
      });
    });
    toRawValues(payload.metafields?.family).forEach((value) => add('metafield-family', 'custom.device_family', value));
    toRawValues(payload.metafields?.model).forEach((value) => add('metafield-model', 'custom.device_model', value));
    toRawValues(payload.metafields?.generation).forEach((value) => add('metafield-generation', 'custom.device_generation', value));
    return sources;
  };

  const rawGenerationCandidates = (source, modelKey) => {
    const candidates = [];
    const seen = new Set();
    const add = (raw, start = 0, end = normalizeText(source.context).length) => {
      const value = normalizeText(raw);
      const tokenKey = `${start}:${end}:${comparisonKey(value)}`;
      if (value && !seen.has(tokenKey)) {
        seen.add(tokenKey);
        candidates.push({ raw: value, start, end });
      }
    };

    if (source.kind === 'metafield-generation') {
      add(source.value, 0, source.context.length);
    }

    const patterns = [...(GENERATION_PATTERNS[modelKey] || [])];
    if (sourceSupportsBareEdition(source, modelKey)) {
      patterns.push(...(BARE_OPTION_PATTERNS[modelKey] || []));
    }
    patterns.forEach((pattern) => {
      const clone = new RegExp(pattern.source, pattern.flags);
      for (const match of source.context.matchAll(clone)) {
        const tail = source.context.slice((match.index || 0) + match[0].length);
        const trailingEdition = PHONE_MODEL_KEYS.has(modelKey)
          ? tail.match(TRAILING_FIT_EDITION_PATTERN)?.[0] || ''
          : '';
        const raw = trailingEdition ? `${match[0]}${trailingEdition}` : match[0];
        add(raw, match.index || 0, (match.index || 0) + raw.length);
      }
    });

    return candidates;
  };

  const choicesForSourceAndModel = (source, model, registry, record) => rawGenerationCandidates(source, model.key)
    .filter(({ raw }) => (
      source.kind !== 'option'
      || DEVICE_OPTION_NAME_PATTERN.test(source.name)
      || DEVICE_SIZE_OPTION_PATTERN.test(source.name)
      || detectModels(raw, registry).has(model.key)
    ))
    .map((candidate) => {
    const { raw, start, end } = candidate;
    const canonical = canonicalGeneration(raw, model.family, model.key, registry);
    const choice = canonical
      ? mappedGenerationChoice(canonical)
      : unknownGenerationChoice(raw, { family: model.family, model: model.key, record, source });
    return {
      ...choice,
      raw,
      start,
      end,
      explicitScope: detectModels(raw, registry).has(model.key)
    };
    });

  const unknownFamilyChoice = (raw, record, source) => {
    const label = normalizeText(raw);
    logUnmapped({ kind: 'family', raw: label, record, source });
    return { key: unknownFamilyKey(label), label, mapped: false };
  };

  const unknownModelChoice = (raw, family, record, source) => {
    const label = normalizeText(raw);
    logUnmapped({ kind: 'model', raw: label, family, record, source });
    return { key: unknownModelKey(label), label, family, mapped: false };
  };

  const createProductData = (payload, registry, record = {}) => {
    const sources = sourceListForPayload(payload);
    const families = new Set();
    const familyChoices = new Map();
    const modelChoices = new Map();
    const identities = new Map();

    sources.filter((source) => source.kind === 'metafield-family').forEach((source) => {
      const mapped = canonicalFamily(source.value, registry);
      const choice = mapped || unknownFamilyChoice(source.value, record, source);
      families.add(choice.key);
      familyChoices.set(choice.key, choice);
    });

    sources.forEach((source) => {
      detectModels(source.context, registry).forEach((model) => {
        modelChoices.set(model.key, { ...model, mapped: true });
        families.add(model.family);
        familyChoices.set(model.family, registry.familyByKey.get(model.family));
      });
    });

    sources.filter((source) => source.kind === 'metafield-model').forEach((source) => {
      const candidateFamilies = families.size ? [...families] : [...inferFamilies(source.value)];
      const mapped = candidateFamilies.map((family) => canonicalModel(source.value, family, registry)).find(Boolean)
        || canonicalModel(source.value, '', registry);
      if (mapped) {
        modelChoices.set(mapped.key, { ...mapped, mapped: true });
        families.add(mapped.family);
        familyChoices.set(mapped.family, registry.familyByKey.get(mapped.family));
        return;
      }

      const family = candidateFamilies.length === 1 ? candidateFamilies[0] : 'accessories';
      const choice = unknownModelChoice(source.value, family, record, source);
      modelChoices.set(choice.key, choice);
      families.add(family);
      if (!familyChoices.has(family)) {
        familyChoices.set(family, registry.familyByKey.get(family));
      }
    });

    if (!families.size) {
      sources.forEach((source) => {
        inferFamilies(source.context).forEach((family) => {
          families.add(family);
          familyChoices.set(family, registry.familyByKey.get(family));
        });
      });
    }
    if (!families.size) {
      families.add('accessories');
      familyChoices.set('accessories', registry.familyByKey.get('accessories'));
    }

    const knownGlobalModels = [...modelChoices.values()].filter((model) => model.mapped);
    const structuredModels = [...modelChoices.values()].filter((model) => {
      if (!model.mapped) {
        return true;
      }
      return sources.some((source) => source.kind === 'metafield-model' && canonicalModel(source.value, model.family, registry)?.key === model.key);
    });

    sources.filter((source) => !['metafield-family', 'metafield-model'].includes(source.kind)).forEach((source) => {
      let candidateModels = [...detectModels(source.context, registry).values()];
      let directScope = candidateModels.length > 0;

      if (!candidateModels.length && source.kind === 'metafield-generation' && structuredModels.length === 1) {
        candidateModels = structuredModels;
        directScope = true;
      }

      if (!candidateModels.length) {
        if (knownGlobalModels.length === 1) {
          candidateModels = knownGlobalModels;
        } else if (knownGlobalModels.length > 1) {
          candidateModels = knownGlobalModels;
        }
      }

      const evaluations = candidateModels.map((model) => ({
        model,
        choices: choicesForSourceAndModel(source, model, registry, record)
      }));
      let acceptedPairs = [];

      if (evaluations.length === 1) {
        const [{ model, choices }] = evaluations;
        const mappedChoices = choices.filter((choice) => choice.mapped);
        const accepted = directScope
          ? choices
          : mappedChoices.length
            ? mappedChoices
            : knownGlobalModels.length === 1 && GLOBAL_UNKNOWN_MODEL_FALLBACK.has(model.key)
              ? choices
              : [];
        acceptedPairs = accepted.map((generation) => ({ model, generation }));
      } else if (evaluations.length > 1) {
        const allPairs = evaluations.flatMap(({ model, choices }) => choices
          .map((generation) => ({ model, generation })));
        const explicitPairs = allPairs.filter(({ generation }) => generation.explicitScope);
        const unambiguousPairs = allPairs.filter((pair) => !allPairs.some((other) => (
          other.model.key !== pair.model.key
          && pair.generation.start < other.generation.end
          && other.generation.start < pair.generation.end
        )));
        if (directScope && (explicitPairs.length || unambiguousPairs.length)) {
          const distinctPairs = new Map();
          [...explicitPairs, ...unambiguousPairs].forEach((pair) => {
            distinctPairs.set(`${pair.model.key}|${pair.generation.key}`, pair);
          });
          acceptedPairs = [...distinctPairs.values()];
        }
        if (!acceptedPairs.length) {
          const mappedPairs = evaluations.flatMap(({ model, choices }) => choices
            .filter((choice) => choice.mapped)
            .map((generation) => ({ model, generation })));
          const mappedModels = new Set(mappedPairs.map(({ model }) => model.key));
          if (mappedModels.size === 1) {
            acceptedPairs = mappedPairs;
          } else if (mappedModels.size > 1) {
            logUnmapped({
              kind: 'ambiguous-generation',
              raw: source.value,
              record,
              source
            });
          }
        }
      }

      acceptedPairs.forEach(({ model, generation }) => {
          const identityKey = [model.family, model.key, generation.key].join('|');
          if (!identities.has(identityKey)) {
            identities.set(identityKey, {
              family: model.family,
              model: model.key,
              generation,
              source: { id: source.id, name: source.name, raw: source.value }
            });
          }
      });
    });

    return {
      id: record.id || '',
      handle: record.handle || '',
      families,
      familyChoices,
      models: new Set(modelChoices.keys()),
      modelChoices,
      identities: [...identities.values()]
    };
  };

  const payloadFromCard = (card) => {
    const source = card.querySelector(':scope > [data-av-device-source]');
    if (source) {
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        console.warn('[Avontic device registry] Product source JSON could not be read; using the legacy card hints.', error);
      }
    }
    return {
      title: card.dataset.avDiscoveryTerms || '',
      productType: '',
      templateSuffix: '',
      tags: [],
      options: [],
      metafields: {
        family: card.dataset.avDeviceFamily || '',
        model: card.dataset.avDeviceModels || '',
        generation: card.dataset.avDeviceGenerations || ''
      }
    };
  };

  const sortGenerationChoices = (choices) => choices.sort((left, right) => {
    return (Number(right.rank) || -1) - (Number(left.rank) || -1)
      || Number(right.mapped) - Number(left.mapped)
      || left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' });
  });

  const manifestCacheKey = (registryVersion) => {
    const url = new URL(window.location.href);
    ['page', 'section_id', 'sections', 'sections_url', 'av_device', 'av_model', 'av_generation'].forEach((key) => url.searchParams.delete(key));
    url.searchParams.sort();
    return `${registryVersion}|${url.pathname}?${url.searchParams.toString()}`;
  };

  const getManifestCache = (key) => {
    const cached = MANIFEST_CACHE.get(key);
    if (!cached || cached.expires <= Date.now()) {
      MANIFEST_CACHE.delete(key);
      return null;
    }
    return cached.data;
  };

  const setManifestCache = (key, data) => {
    MANIFEST_CACHE.set(key, { data, expires: Date.now() + MANIFEST_TTL });
    while (MANIFEST_CACHE.size > MAX_MANIFEST_CACHE_ENTRIES) {
      MANIFEST_CACHE.delete(MANIFEST_CACHE.keys().next().value);
    }
  };

  class AvonticDiscoveryRefiner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.target = document.getElementById(this.dataset.targetId);
      if (!this.target) {
        return;
      }
      this.cards = [...this.target.children].filter((node) => node.matches?.('product-card.product-card'));
      if (!this.cards.length) {
        return;
      }

      try {
        this.registry = parseRegistry(this);
      } catch (error) {
        console.error('[Avontic device registry] Discovery could not initialize.', error);
        return;
      }

      this.initialized = true;
      this.controller = new AbortController();
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.nodes = [...this.target.children];
      this.nodePositions = new Map(this.nodes.map((node, index) => [node, index]));
      const productSlots = this.nodes
        .map((node, index) => node.matches?.('product-card.product-card') ? index : null)
        .filter((index) => index !== null);
      [...this.cards].sort((left, right) => {
        const leftRank = COMMERCIAL_TIER_ORDER[left.dataset.avCommercialTier] ?? COMMERCIAL_TIER_ORDER.maintain;
        const rightRank = COMMERCIAL_TIER_ORDER[right.dataset.avCommercialTier] ?? COMMERCIAL_TIER_ORDER.maintain;
        return leftRank - rightRank || this.nodePositions.get(left) - this.nodePositions.get(right);
      }).forEach((card, index) => this.nodePositions.set(card, productSlots[index]));

      this.familySelect = this.querySelector('[data-av-discovery-filter="family"]');
      this.modelSelect = this.querySelector('[data-av-discovery-filter="model"]');
      this.generationSelect = this.querySelector('[data-av-discovery-filter="generation"]');
      this.clearButton = this.querySelector('[data-av-discovery-clear]');
      this.status = this.querySelector('[data-av-discovery-status]');
      this.breadcrumb = this.querySelector('[data-av-discovery-breadcrumb]');
      this.selection = this.querySelector('[data-av-discovery-selection]');
      this.selectionTitle = this.querySelector('[data-av-discovery-selection-title]');
      this.chips = this.querySelector('[data-av-discovery-chips]');
      this.placeholders = {
        family: this.familySelect.options[0].textContent,
        model: this.modelSelect.options[0].textContent,
        generation: this.generationSelect.options[0].textContent
      };
      this.cardData = new Map(this.cards.map((card) => [
        card,
        createProductData(payloadFromCard(card), this.registry, {
          id: card.dataset.productId,
          handle: card.dataset.avProductHandle
        })
      ]));

      this.createAlternatives();
      this.readStateFromUrl(true);
      this.populateControls();
      this.bindEvents();
      this.hidden = false;
      this.apply();
      this.scheduleManifestLoad();

      window.AvonticDeviceDiscovery = {
        comparisonKey,
        registryVersion: this.registry.version,
        unmapped: ensureUnmappedLog()
      };
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.idleHandle && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.idleHandle);
      }
      if (this.timerHandle) {
        window.clearTimeout(this.timerHandle);
      }
    }

    createAlternatives() {
      this.alternatives = document.createElement('section');
      this.alternatives.className = 'av-discovery-alternatives';
      this.alternatives.hidden = true;
      this.alternativeDetails = document.createElement('details');
      this.alternativeSummary = document.createElement('summary');
      this.alternativeList = document.createElement(this.target.localName);
      this.alternativeList.className = `${this.target.className} av-discovery-alternatives__grid`;
      ['collection-mobile-layout', 'collection-desktop-layout'].forEach((attribute) => {
        if (this.target.hasAttribute(attribute)) {
          this.alternativeList.setAttribute(attribute, this.target.getAttribute(attribute));
        }
      });
      this.alternativeDetails.append(this.alternativeSummary, this.alternativeList);
      this.alternatives.append(this.alternativeDetails);
      this.target.after(this.alternatives);
    }

    inferFromQuery() {
      const query = normalizeText(this.dataset.query);
      if (!query) {
        return { family: '', model: '', generation: '', modelChoice: null, generationChoice: null };
      }

      const models = [...detectModels(query, this.registry).values()];
      const families = new Set(models.map((model) => model.family));
      inferFamilies(query).forEach((family) => families.add(family));
      const model = models.length === 1 ? models[0] : null;
      let generationChoice = null;
      if (model) {
        const source = { id: 'query', kind: 'title', name: 'page query', value: query, context: query };
        generationChoice = choicesForSourceAndModel(source, model, this.registry, { handle: 'page-query' })
          .find((choice) => choice.mapped) || null;
      }

      return {
        family: families.size === 1 ? [...families][0] : model?.family || '',
        model: model?.key || '',
        generation: generationChoice?.key || '',
        modelChoice: model ? { ...model, mapped: true } : null,
        generationChoice
      };
    }

    readStateFromUrl(allowInference) {
      const params = new URL(window.location.href).searchParams;
      const inferred = allowInference ? this.inferFromQuery() : { family: '', model: '', generation: '' };
      const requestedFamily = params.get('av_device');
      const requestedModel = params.get('av_model');
      const requestedGeneration = params.get('av_generation');
      this.unresolvedGenerationChoice = null;

      this.explicitAll = requestedFamily === 'all';
      this.autoInferred = allowInference
        && !params.has('av_device')
        && !params.has('av_model')
        && !params.has('av_generation')
        && Boolean(inferred.family || inferred.model || inferred.generation);

      let familyChoice = null;
      if (!this.explicitAll && requestedFamily) {
        familyChoice = canonicalFamily(requestedFamily, this.registry);
        if (!familyChoice) {
          familyChoice = unknownFamilyChoice(requestedFamily, { handle: 'url' }, { id: 'url-family', name: 'av_device' });
        }
      } else if (!this.explicitAll && inferred.family) {
        familyChoice = this.registry.familyByKey.get(inferred.family) || null;
      }

      let modelChoice = null;
      if (requestedModel) {
        modelChoice = canonicalModel(requestedModel, familyChoice?.key || '', this.registry);
        if (!modelChoice) {
          const family = familyChoice?.key || inferred.family || 'accessories';
          modelChoice = unknownModelChoice(requestedModel, family, { handle: 'url' }, { id: 'url-model', name: 'av_model' });
        }
      } else if (!this.explicitAll && inferred.modelChoice) {
        modelChoice = inferred.modelChoice;
      }

      if (modelChoice?.family && !familyChoice) {
        familyChoice = this.registry.familyByKey.get(modelChoice.family) || null;
      }

      let generationChoice = null;
      if (requestedGeneration && modelChoice) {
        const mapped = canonicalGeneration(requestedGeneration, modelChoice.family, modelChoice.key, this.registry);
        generationChoice = mapped
          ? mappedGenerationChoice(mapped)
          : unknownGenerationChoice(requestedGeneration, {
              family: modelChoice.family,
              model: modelChoice.key,
              record: { handle: 'url' },
              source: { id: 'url-generation', name: 'av_generation' }
            });
      } else if (requestedGeneration) {
        this.unresolvedGenerationChoice = unknownGenerationChoice(requestedGeneration, {
          family: familyChoice?.key || '',
          model: '',
          record: { handle: 'url' },
          source: { id: 'url-generation-unscoped', name: 'av_generation' }
        });
      } else if (!this.explicitAll && inferred.generationChoice && modelChoice?.key === inferred.model) {
        generationChoice = inferred.generationChoice;
      }

      this.state = {
        family: familyChoice?.key || '',
        familyChoice,
        model: modelChoice?.key || '',
        modelChoice,
        generation: generationChoice?.key || '',
        generationChoice
      };
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        const select = event.target.closest?.('[data-av-discovery-filter]');
        if (!select) {
          return;
        }

        const key = select.dataset.avDiscoveryFilter;
        this.explicitAll = false;
        this.autoInferred = false;
        this.unresolvedGenerationChoice = null;
        if (key === 'family') {
          this.state.family = select.value;
          this.state.familyChoice = this.currentFamilyChoices.get(select.value) || null;
          this.state.model = '';
          this.state.modelChoice = null;
          this.state.generation = '';
          this.state.generationChoice = null;
        } else if (key === 'model') {
          this.state.model = select.value;
          this.state.modelChoice = this.currentModelChoices.get(select.value) || null;
          this.state.generation = '';
          this.state.generationChoice = null;
        } else {
          this.state.generation = select.value;
          this.state.generationChoice = this.currentGenerationChoices.get(select.value) || null;
        }

        if (this.navigateToFirstPage()) {
          return;
        }
        this.populateControls();
        this.apply();
        this.ensureManifest(true);
      }, { signal: this.controller.signal });

      this.clearButton.addEventListener('click', () => {
        this.state = {
          family: '', familyChoice: null,
          model: '', modelChoice: null,
          generation: '', generationChoice: null
        };
        this.explicitAll = true;
        this.autoInferred = false;
        this.unresolvedGenerationChoice = null;
        this.populateControls();
        this.apply();
        this.familySelect.focus();
      }, { signal: this.controller.signal });

      window.addEventListener('popstate', () => {
        this.readStateFromUrl(true);
        this.populateControls();
        this.apply(false);
        this.scheduleManifestLoad();
      }, { signal: this.controller.signal });
    }

    controlData() {
      return this.fullData || [...this.cardData.values()];
    }

    populateControls() {
      const data = this.controlData();
      const familyChoices = new Map();
      data.forEach((product) => product.familyChoices.forEach((choice, key) => familyChoices.set(key, choice)));
      if (this.state.familyChoice) {
        familyChoices.set(this.state.familyChoice.key, this.state.familyChoice);
      }
      const knownFamilies = FAMILY_ORDER
        .filter((key) => familyChoices.has(key))
        .map((key) => {
          const registryChoice = familyChoices.get(key);
          const datasetLabel = this.dataset[`family${key[0].toUpperCase()}${key.slice(1)}`];
          return { ...registryChoice, label: datasetLabel || registryChoice.label };
        });
      const unknownFamilies = [...familyChoices.values()]
        .filter((choice) => !FAMILY_ORDER.includes(choice.key))
        .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }));
      const familyList = [...knownFamilies, ...unknownFamilies];
      this.currentFamilyChoices = new Map(familyList.map((choice) => [choice.key, choice]));
      this.populateSelect(this.familySelect, this.placeholders.family, familyList, this.state.family);

      const modelChoices = new Map();
      data.forEach((product) => {
        product.modelChoices.forEach((choice, key) => {
          if (!this.state.family || choice.family === this.state.family) {
            modelChoices.set(key, choice);
          }
        });
      });
      if (this.state.modelChoice) {
        modelChoices.set(this.state.modelChoice.key, this.state.modelChoice);
      }
      const modelList = [...modelChoices.values()].sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' }));
      this.currentModelChoices = new Map(modelList.map((choice) => [choice.key, choice]));
      this.populateSelect(this.modelSelect, this.placeholders.model, modelList, this.state.model);

      const generationChoices = new Map();
      if (this.state.model) {
        data.forEach((product) => {
          product.identities.forEach((identity) => {
            if (identity.model === this.state.model && (!this.state.family || identity.family === this.state.family)) {
              generationChoices.set(identity.generation.key, identity.generation);
            }
          });
        });
      }
      if (this.state.generationChoice) {
        generationChoices.set(this.state.generationChoice.key, this.state.generationChoice);
      }
      const generationList = sortGenerationChoices([...generationChoices.values()]);
      this.currentGenerationChoices = new Map(generationList.map((choice) => [choice.key, choice]));
      this.populateSelect(this.generationSelect, this.placeholders.generation, generationList, this.state.generation);

      this.modelSelect.disabled = !this.state.family || modelList.length === 0;
      this.generationSelect.disabled = !this.state.model || generationList.length === 0;
    }

    populateSelect(select, placeholder, choices, selectedValue) {
      const fragment = document.createDocumentFragment();
      fragment.append(new Option(placeholder, ''));
      choices.forEach(({ key, label }) => fragment.append(new Option(label, key)));
      select.replaceChildren(fragment);
      select.value = choices.some((choice) => choice.key === selectedValue) ? selectedValue : '';
    }

    isDataMatch(data) {
      if (this.state.family && !data.families.has(this.state.family)) {
        return false;
      }
      if (this.state.model && !data.models.has(this.state.model)) {
        return false;
      }
      if (this.state.generation) {
        return data.identities.some((identity) => (
          identity.generation.key === this.state.generation
          && (!this.state.family || identity.family === this.state.family)
          && (!this.state.model || identity.model === this.state.model)
        ));
      }
      return true;
    }

    selectionLabels() {
      return [
        this.state.familyChoice?.label,
        this.state.modelChoice?.label,
        this.state.generationChoice?.label,
        this.unresolvedGenerationChoice?.label
      ].filter(Boolean);
    }

    renderSelection() {
      const labels = this.selectionLabels();
      const active = labels.length > 0;
      this.breadcrumb.hidden = !active;
      this.selection.hidden = !active;
      if (!active) {
        this.breadcrumb.querySelector('ol').replaceChildren();
        this.chips.replaceChildren();
        this.selectionTitle.textContent = '';
        return;
      }

      const breadcrumbLabels = [this.dataset.breadcrumbAll, ...labels];
      const breadcrumbFragment = document.createDocumentFragment();
      breadcrumbLabels.forEach((label, index) => {
        const item = document.createElement('li');
        item.textContent = label;
        if (index === breadcrumbLabels.length - 1) {
          item.setAttribute('aria-current', 'page');
        }
        breadcrumbFragment.append(item);
      });
      this.breadcrumb.querySelector('ol').replaceChildren(breadcrumbFragment);

      const selectionText = labels.join(' / ');
      this.selectionTitle.textContent = String(this.dataset.selectionTitle || '').replace('__SELECTION__', selectionText);
      const chipFragment = document.createDocumentFragment();
      labels.forEach((label) => {
        const chip = document.createElement('span');
        chip.className = 'av-discovery-refiner__chip';
        chip.textContent = label;
        chipFragment.append(chip);
      });
      this.chips.replaceChildren(chipFragment);
    }

    apply(updateUrl = true) {
      const active = activeState(this.state);
      const unresolvedGeneration = this.unresolvedGenerationChoice;
      const matchingCards = active ? this.cards.filter((card) => this.isDataMatch(this.cardData.get(card))) : [...this.cards];
      const alternativeCards = active ? this.cards.filter((card) => !this.isDataMatch(this.cardData.get(card))) : [];
      const fullMatches = active && this.fullData ? this.fullData.filter((data) => this.isDataMatch(data)) : [];
      const firstRemoteMatch = !unresolvedGeneration && matchingCards.length === 0 && fullMatches.length > 0 ? fullMatches[0] : null;
      const matchingSet = new Set(matchingCards);
      const primaryNodes = this.nodes
        .filter((node) => !node.matches?.('product-card.product-card') || matchingSet.has(node))
        .sort((left, right) => this.nodePositions.get(left) - this.nodePositions.get(right));

      this.target.append(...primaryNodes);
      this.alternativeList.append(...alternativeCards);
      this.alternatives.hidden = alternativeCards.length === 0;
      this.alternativeDetails.open = !firstRemoteMatch && matchingCards.length === 0 && alternativeCards.length > 0;
      this.alternativeSummary.textContent = this.formatCount(
        this.dataset.alternativeResults,
        alternativeCards.length,
        this.dataset.alternativeResultsOne
      );
      this.clearButton.hidden = !active && !unresolvedGeneration;
      this.renderSelection();

      if (unresolvedGeneration) {
        this.status.textContent = String(this.dataset.unresolvedGeneration || '')
          .replace('__VALUE__', unresolvedGeneration.label);
      } else if (!active) {
        this.status.textContent = '';
      } else if (this.fullData) {
        this.status.textContent = fullMatches.length === 0
          ? this.formatSelection(this.dataset.noExactSelection || this.dataset.noExactMatches)
          : this.formatCount(this.dataset.matchStatusFull, fullMatches.length, this.dataset.matchStatusFullOne);
      } else if (!this.manifestUnavailable) {
        const pageStatus = matchingCards.length === 0
          ? this.formatSelection(this.dataset.noPageMatches)
          : this.formatCount(this.dataset.matchStatus, matchingCards.length, this.dataset.matchStatusOne);
        this.status.textContent = `${pageStatus} ${this.dataset.matchStatusLoading || ''}`.trim();
      } else if (matchingCards.length === 0) {
        this.status.textContent = this.formatSelection(this.dataset.noExactSelection || this.dataset.noExactMatches);
      } else {
        this.status.textContent = this.formatCount(this.dataset.matchStatus, matchingCards.length, this.dataset.matchStatusOne);
      }

      if (this.autoInferred && active && this.status.textContent) {
        this.status.textContent = `${this.status.textContent} ${this.dataset.inferredStatus || ''}`.trim();
      }
      if (firstRemoteMatch) {
        const exactPageLink = this.createExactPageLink(firstRemoteMatch);
        if (exactPageLink) {
          this.status.append(' ', exactPageLink);
        }
      }
      if (updateUrl) {
        this.updateUrl();
      }
      this.syncNativeForms();
      this.syncPaginationLinks();
    }

    stateUrlValues() {
      return {
        av_device: this.explicitAll ? 'all' : this.state.family,
        av_model: this.state.model,
        av_generation: this.state.generationChoice
          ? (this.state.generationChoice.mapped ? this.state.generationChoice.key : this.state.generationChoice.urlValue)
          : this.unresolvedGenerationChoice?.urlValue || ''
      };
    }

    navigateToFirstPage() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('page')) {
        return false;
      }
      Object.entries(this.stateUrlValues()).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        } else {
          url.searchParams.delete(key);
        }
      });
      url.searchParams.delete('page');
      window.location.assign(url);
      return true;
    }

    updateUrl() {
      const url = new URL(window.location.href);
      Object.entries(this.stateUrlValues()).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        } else {
          url.searchParams.delete(key);
        }
      });
      window.history.replaceState(window.history.state, '', url);
    }

    syncNativeForms() {
      const values = this.stateUrlValues();
      this.section?.querySelectorAll('facets-form form').forEach((form) => {
        Object.entries(values).forEach(([name, value]) => {
          let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
          if (!value) {
            input?.remove();
            return;
          }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.append(input);
          }
          input.value = value;
        });
      });
    }

    syncPaginationLinks() {
      const values = this.stateUrlValues();
      this.section?.querySelectorAll('a[href]').forEach((link) => {
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.searchParams.has('page')) {
          return;
        }
        Object.entries(values).forEach(([key, value]) => {
          if (value) {
            url.searchParams.set(key, value);
          } else {
            url.searchParams.delete(key);
          }
        });
        link.href = url;
      });
    }

    createExactPageLink(match) {
      const pageSize = Math.max(1, Number(this.dataset.pageSize) || this.cards.length || 1);
      const page = Math.floor((Number(match.catalogIndex) || 0) / pageSize) + 1;
      const url = new URL(window.location.href);
      Object.entries(this.stateUrlValues()).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        } else {
          url.searchParams.delete(key);
        }
      });
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      const link = document.createElement('a');
      link.href = url;
      link.textContent = String(this.dataset.exactPageLink || 'View exact matches on page __PAGE__')
        .replace('__PAGE__', String(page));
      return link;
    }

    scheduleManifestLoad() {
      if (!activeState(this.state) || this.fullData || this.manifestPromise || this.manifestUnavailable) {
        return;
      }
      const start = () => {
        if (this.isConnected && activeState(this.state)) {
          this.ensureManifest(true);
        }
      };
      if ('requestIdleCallback' in window) {
        this.idleHandle = window.requestIdleCallback(start, { timeout: 2200 });
      } else {
        this.timerHandle = window.setTimeout(start, 1400);
      }
    }

    async ensureManifest(immediate = false) {
      if (!activeState(this.state) || this.fullData || this.manifestPromise || this.manifestUnavailable) {
        return;
      }
      if (immediate) {
        if (this.idleHandle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.idleHandle);
        }
        if (this.timerHandle) {
          window.clearTimeout(this.timerHandle);
        }
      }

      const cacheKey = manifestCacheKey(this.registry.version);
      const cached = getManifestCache(cacheKey);
      if (cached) {
        this.fullData = cached;
        this.populateControls();
        this.apply(false);
        return;
      }

      this.manifestPromise = this.fetchManifest()
        .then((data) => {
          if (!this.isConnected) {
            return;
          }
          this.fullData = data;
          setManifestCache(cacheKey, data);
          this.populateControls();
          this.apply(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') {
            return;
          }
          this.manifestUnavailable = true;
          console.warn('[Avontic device registry] Collection-wide count is unavailable; page-local matching remains active.', error);
          if (this.isConnected) {
            this.apply(false);
          }
        })
        .finally(() => {
          this.manifestPromise = null;
        });
      await this.manifestPromise;
    }

    async fetchManifest() {
      const seenProducts = new Set();
      const data = [];
      const first = await this.fetchManifestPage(1);
      const pages = Math.max(1, Number(first.pages) || 1);
      const manifests = [first];

      for (let page = 2; page <= pages; page += 1) {
        manifests.push(await this.fetchManifestPage(page));
      }

      for (const manifest of manifests) {
        for (const item of manifest.items || []) {
          const productKey = String(item.id || item.handle || '');
          if (!productKey || seenProducts.has(productKey)) {
            continue;
          }
          seenProducts.add(productKey);
          const productData = createProductData(item, this.registry, { id: item.id, handle: item.handle });
          productData.catalogIndex = Number.isFinite(Number(item.catalogIndex)) ? Number(item.catalogIndex) : data.length;
          data.push(productData);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }
      return data;
    }

    async fetchManifestPage(page) {
      const url = new URL(window.location.href);
      ['av_device', 'av_model', 'av_generation', 'sections', 'sections_url'].forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('section_id', 'avontic-device-manifest');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: this.controller.signal
      });
      if (!response.ok) {
        throw new Error(`Device manifest request failed with HTTP ${response.status}.`);
      }
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const node = documentFragment.querySelector('[data-av-device-manifest]');
      if (!node) {
        throw new Error('Device manifest response did not contain JSON.');
      }
      return JSON.parse(node.textContent);
    }

    formatCount(template, count, singularTemplate = '') {
      const selectedTemplate = count === 1 && singularTemplate ? singularTemplate : template;
      return String(selectedTemplate || '').replace('__COUNT__', String(count));
    }

    formatSelection(template) {
      const selection = this.selectionLabels().join(' / ');
      return String(template || '').replace('__SELECTION__', selection);
    }
  }

  if (!customElements.get('avontic-discovery-refiner')) {
    customElements.define('avontic-discovery-refiner', AvonticDiscoveryRefiner);
  }
})();
